// Decide what Lex/NLU deliverable a Design Model warrants, so the Design tab produces the right
// artifacts automatically (not every project needs a Lex bot, and only large NLU bots warrant a
// standalone design worksheet). Pure + dependency-free so it's trivially testable and usable from any
// of the worksheet builders.
//
// Three outcomes:
//   NO_LEX          — DTMF-only (touch-tone menu). No Lex bot; the DDD's core Connect config is enough.
//   SIMPLE_IN_DDD   — a small speech bot. Its config lives in the DDD's Amazon Lex / Lex Bot Intents /
//                     Lex Bot Slots & Types tabs — no separate document.
//   STANDALONE_NLU  — a large NLU bot (many intents / slots / languages). Warrants the standalone NLU
//                     design worksheet (the Viamericas-style deliverable) in addition to the DDD tabs.

export const LEX_STRATEGY = {
  NO_LEX: 'NO_LEX',
  SIMPLE_IN_DDD: 'SIMPLE_IN_DDD',
  STANDALONE_NLU: 'STANDALONE_NLU',
};

// Conservative thresholds for "this is a large NLU bot worth its own worksheet". A bot stays SIMPLE
// (lives in the DDD) only when it is small on EVERY axis; cross any one and it becomes STANDALONE.
const SIMPLE_MAX_INTENTS = 3;
const SIMPLE_MAX_LANGUAGES = 1;

function countUtterances(intent) {
  const u = intent && intent.utterances;
  if (!u || typeof u !== 'object') return 0;
  return Object.values(u).reduce((n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0);
}

/**
 * @param {object} model  Design Model
 * @returns {{ strategy: string, needsLex: boolean, standalone: boolean, reason: string, signals: object }}
 */
export function decideLexStrategy(model) {
  const m = model || {};
  const meta = m.meta || {};
  const intents = Array.isArray(m.intents) ? m.intents : [];
  const lexBots = Array.isArray(m.lexBots) ? m.lexBots : [];
  const slotTypes = Array.isArray(m.slotTypes) ? m.slotTypes : [];
  const languages = Array.isArray(meta.languages) && meta.languages.length ? meta.languages : ['en'];

  const interactionStyle = meta.interactionStyle; // 'dtmf_only' | 'natural_language' | 'hybrid' | undefined
  const intentCount = intents.length;
  const hasSlots = intents.some((i) => Array.isArray(i.slots) && i.slots.length > 0);
  const hasCustomSlotTypes = slotTypes.length > 0;
  const hasUtterances = intents.some((i) => countUtterances(i) > 0);

  // Stage 2.8: production Lex controls (progressive elicitation, DTMF/read-back slots, persona intent
  // permissions, disambiguation / session-management / fallback policy) are the hallmark of a bot that
  // warrants its own NLU design worksheet — not something the DDD's small Lex tabs can hold.
  const hasElicitationConfig = intents.some((i) =>
    Array.isArray(i.slots) && i.slots.some((s) =>
      s && (s.elicitationMode || s.prompts || s.dtmfEntry || s.confirmationReadback ||
        (Array.isArray(s.runtimeHints) && s.runtimeHints.length))));
  const hasPersonaPermissions = (Array.isArray(m.personas) ? m.personas : []).some((p) =>
    p && p.intentPermissions && ((p.intentPermissions.selfService || []).length || (p.intentPermissions.routeOnly || []).length));
  const hasBotPolicy = lexBots.some((b) =>
    b && (b.disambiguation || b.sessionManagement || b.fallback || b.confidenceThresholds || b.dialogHookRef));
  const hasRichLexConfig = hasElicitationConfig || hasPersonaPermissions || hasBotPolicy;

  const signals = {
    interactionStyle: interactionStyle || '(unset)',
    lexBots: lexBots.length,
    intents: intentCount,
    languages: languages.length,
    hasSlots,
    hasCustomSlotTypes,
    hasUtterances,
    hasElicitationConfig,
    hasPersonaPermissions,
    hasBotPolicy,
  };

  // No Lex bot: explicit DTMF-only, or nothing indicates natural-language understanding is in scope.
  const naturalStyle = interactionStyle === 'natural_language' || interactionStyle === 'hybrid';
  const needsLex = interactionStyle === 'dtmf_only'
    ? false
    : naturalStyle || lexBots.length > 0 || hasUtterances;

  if (!needsLex) {
    return {
      strategy: LEX_STRATEGY.NO_LEX,
      needsLex: false,
      standalone: false,
      reason: interactionStyle === 'dtmf_only'
        ? 'DTMF-only interaction — no Lex bot required.'
        : 'No natural-language understanding indicated — DTMF/core Connect config only.',
      signals,
    };
  }

  const isSimple =
    intentCount <= SIMPLE_MAX_INTENTS &&
    !hasSlots &&
    !hasCustomSlotTypes &&
    !hasRichLexConfig &&
    languages.length <= SIMPLE_MAX_LANGUAGES;

  return isSimple
    ? {
        strategy: LEX_STRATEGY.SIMPLE_IN_DDD,
        needsLex: true,
        standalone: false,
        reason: `Small speech bot (${intentCount} intent(s), no custom slots, ${languages.length} language) — captured in the DDD's Lex tabs.`,
        signals,
      }
    : {
        strategy: LEX_STRATEGY.STANDALONE_NLU,
        needsLex: true,
        standalone: true,
        reason: `Large NLU bot (${intentCount} intent(s)${hasSlots ? ', slotted' : ''}${hasCustomSlotTypes ? ', custom slot types' : ''}${hasRichLexConfig ? ', production elicitation/persona/session config' : ''}, ${languages.length} language(s)) — warrants a standalone NLU design worksheet.`,
        signals,
      };
}

export default decideLexStrategy;
